import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowRight, Check, ChevronRight, Flower2, Pause, Play, Sprout, X } from "lucide-react";
import ShoeArt from "./ShoeArt";
import { SIGN_UP_URL } from "./links";
import { useReducedMotion } from "./motion";

const TaskDemo = lazy(() => import("@sprout/ui/wall/TaskDemo3D"));
export const DEMO_STEPS = ["Find your pair", "Slip them on", "Fasten the straps"];
type Beat = 0 | 1 | 2;
export type DemoPhase = "watch" | "try" | "done";

/** The sample chore: watch Buddy, take a turn, earn a flower. No account, camera or data. */
export default function ChoreDemo({ close, start = "watch" }: { close: () => void; start?: DemoPhase }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [phase, setPhase] = useState<DemoPhase>(start);
  const [step, setStep] = useState<Beat>(0),
    [manual, setManual] = useState<Beat>(),
    [play, setPlay] = useState(true);
  const reduced = useReducedMotion();
  useEffect(() => {
    const el = dialog.current;
    el?.showModal();
    return () => el?.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="site-demo"
      aria-labelledby="demo-title"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClick={(e) => {
        // A click on the backdrop closes the sample.
        if (e.target === dialog.current) close();
      }}
    >
      <div className="site-demo-body">
        <header>
          <span className="site-chip">
            <Sprout size={15} /> Sample chore
          </span>
          <button type="button" className="site-icon-btn round" aria-label="Close sample" onClick={close}>
            <X size={19} />
          </button>
        </header>
        <h2 id="demo-title">{phase === "done" ? "Shoes on. You did it!" : "Shoes on"}</h2>
        {phase === "watch" ? (
          <>
            <div className="site-lesson">
              <Suspense fallback={<p>Opening the lesson…</p>}>
                <TaskDemo
                  questId="ready"
                  avatar="fox"
                  reducedMotion={reduced}
                  playing={play}
                  step={manual}
                  onStageChange={setStep}
                />
              </Suspense>
            </div>
            <div className="site-progress" aria-hidden="true">
              {DEMO_STEPS.map((s, i) => (
                <span key={s} className={i < step ? "done" : i === step ? "now" : ""} />
              ))}
            </div>
            <div className="site-beat">
              <span>
                Step {step + 1} of 3 <b>{DEMO_STEPS[step]}</b>
              </span>
              {!reduced && (
                <button
                  type="button"
                  className="site-icon-btn"
                  aria-label={play ? "Pause lesson" : "Play lesson"}
                  onClick={() => setPlay(!play)}
                >
                  {play ? <Pause size={17} /> : <Play size={17} />}
                </button>
              )}
              <button
                type="button"
                className="site-icon-btn"
                aria-label="Next step"
                onClick={() => {
                  const next = ((step + 1) % 3) as Beat;
                  setPlay(false);
                  setStep(next);
                  setManual(next);
                }}
              >
                <ChevronRight size={19} />
              </button>
            </div>
            <button type="button" className="site-btn primary block" onClick={() => setPhase("try")}>
              My turn <ArrowRight size={17} />
            </button>
          </>
        ) : phase === "try" ? (
          <>
            <div className="site-practice">
              <ShoeArt />
              <strong>Try putting on your shoes.</strong>
            </div>
            <button type="button" className="site-btn primary block" onClick={() => setPhase("done")}>
              <Check size={18} /> I’m finished
            </button>
          </>
        ) : (
          <>
            <div className="site-win">
              <span>
                <Flower2 size={62} strokeWidth={1.8} />
              </span>
              <p>A flower for your effort.</p>
            </div>
            <a className="site-btn primary block" href={SIGN_UP_URL}>
              Create a family <ArrowRight size={16} />
            </a>
            <button type="button" className="site-text-btn" onClick={close}>
              Back to website
            </button>
          </>
        )}
      </div>
    </dialog>
  );
}
